import { Component, DestroyRef, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, interval, Observable, Subscription } from 'rxjs';
import { filter, first, map } from 'rxjs/operators';
import { selectUser } from '@platform/platform.selectors';

import { RootState } from '@root/app.state';
import { AuthorizeService } from '@platform/auth/authorize.service';
import * as PlatformActions from '@platform/platform.actions';
import { CurrentUser } from '@platform/platform.state';
import * as CoreActions from '@core/core.actions';
import { selectCustomerSpaceNotifications, selectNavigatedCustomerSpace } from '@core/core.selectors';
import { CustomerSpace } from '@core/core.state';
import { ResourceStatuses } from '@platform/shared/resource.state';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatMenuModule } from '@angular/material/menu';
import { TranslateModule } from '@ngx-translate/core';
import { ShowAuthorizeDirective } from '@platform/auth/show-authorize.directive';
import { UserLogoComponent } from '../user-logo.component';
import { CompanyLogoComponent } from './company-logo.component';
import { OverviewLinkComponent } from './overview-link.component';
import { PortalAdministrationLinkComponent } from './portal-administration-link.component';
import { HomeLinkComponent } from './home-link.component';
import { NotificationButtonComponent } from './notification-button.component';
import { CustomerSpaceAdministrationLinkComponent } from './customer-space-administration-link.component';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatIconModule,
    MatDividerModule,
    MatToolbarModule,
    MatMenuModule,
    TranslateModule,
    ShowAuthorizeDirective,
    UserLogoComponent,
    CompanyLogoComponent,
    OverviewLinkComponent,
    PortalAdministrationLinkComponent,
    HomeLinkComponent,
    NotificationButtonComponent,
    CustomerSpaceAdministrationLinkComponent,
  ],
})
export class HeaderComponent implements OnInit, OnDestroy {
  isSmall$ = new BehaviorSubject<boolean>(false);
  user!: CurrentUser;
  isSmall = this.isSmall$.asObservable();

  isCustomerSpace = false;
  notificationCount$!: Observable<number>;

  showOverviewButton = true;
  customerSpaceName: string; // todo observable?

  customerSpaceId: string | null = null;
  private notificationSubscription!: Subscription;
  private destroyRef = inject(DestroyRef);

  constructor(
    private store$: Store<RootState>,
    route: ActivatedRoute,
    private authorizeService: AuthorizeService,
    private breakpointObserver: BreakpointObserver,
  ) {
    this.isCustomerSpace = !!route.snapshot.paramMap.get('customerSpaceId');
  }

  ngOnInit(): void {
    this.breakpointObserver.observe([Breakpoints.HandsetLandscape, Breakpoints.HandsetPortrait]).subscribe((result) => {
      this.isSmall$.next(result.matches);
    });

    this.notificationCount$ = this.store$.pipe(
      select(selectCustomerSpaceNotifications),
      filter((state) => state.status === ResourceStatuses.SUCCEEDED || state.status === ResourceStatuses.FAILED),
      map((state) => state.data.filter((x) => !x.isRead).length),
    );

    this.store$
      .pipe(
        select(selectNavigatedCustomerSpace),
        filter((x) => !!x.data),
        map((x) => x.data),
        first(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((x) => this.loadCustomerSpaceNotifications(x));

    this.store$
      .pipe(
        select(selectUser),
        map((state) => state.data),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((x) => {
        this.user = x;
        this.showOverviewButton = this.allowedCustomersSpacesCount(this.user) > 1;
      });
  }

  public logout(): void {
    this.store$.dispatch(PlatformActions.logout());
  }

  public showNotifications($event: Event) {
    $event.preventDefault();
    $event.stopPropagation();

    this.store$.dispatch(CoreActions.showNotifications({ show: true }));
  }

  ngOnDestroy(): void {
    this.notificationSubscription?.unsubscribe();
  }

  isAuthorized(): Observable<boolean> {
    const values = new Map<string, string>();
    values.set('customerSpaceId', this.customerSpaceId!);
    return this.authorizeService.isAuthorized('COCKPIT_READ', values);
  }

  private allowedCustomersSpacesCount(user: CurrentUser): number {
    if (!user) {
      return 0;
    }
    const perms = this.user.permissions.filter((p) => p.resourceId.includes('customer-spaces')).map((perm) => perm.resourceId[1]);
    const filteredPerms = perms.filter(Boolean);
    const distinctPermissions = new Set(filteredPerms);

    return distinctPermissions.size;
  }

  private loadCustomerSpaceNotifications(customerSpace: CustomerSpace) {
    if (!this.isCustomerSpace) {
      this.customerSpaceName = '';
      return;
    }

    this.customerSpaceId = customerSpace.id;

    this.store$.dispatch(CoreActions.getCustomerSpaceNotifications({ customerSpaceId: this.customerSpaceId }));

    this.notificationSubscription = interval(1000 * 30).subscribe(() =>
      this.store$.dispatch(CoreActions.getCustomerSpaceNotifications({ customerSpaceId: this.customerSpaceId! })),
    );
    this.customerSpaceName = customerSpace.name;
  }
}
